import { FileText, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'

export interface DocumentFilter {
  /** Titles of the selected documents, in selection order. */
  titles: string[]
  /** Switch to the document view with the selection intact. */
  onOpen: () => void
  onClear: () => void
}

/** How many titles to list before collapsing the rest into "…and N more". */
const MAX_TITLES = 5

/**
 * The document filter as a section of the filter panel. The header chip
 * already shows it, but a filter that narrows this very list also belongs
 * with the other filters, where a reader expects to find out why the count
 * is what it is.
 */
export function DocumentFilterSection({ filter }: { filter: DocumentFilter | null | undefined }) {
  const { t } = useTranslation()
  if (!filter || !filter.titles.length) return null
  const shown = filter.titles.slice(0, MAX_TITLES)
  const rest = filter.titles.length - shown.length
  return (
    <div className="rounded-md border border-primary/40 bg-primary/10 p-2">
      <div className="mb-1 flex items-center justify-between">
        <span className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-primary">
          <FileText className="size-3" />
          {t('termBase.documentFilter')}
        </span>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={filter.onClear}
          title={t('termBase.clearDocumentFilter')}
          aria-label={t('termBase.clearDocumentFilter')}
        >
          <X />
        </Button>
      </div>
      <ul className="space-y-0.5 text-[11px]">
        {shown.map((title, i) => (
          <li key={i}>
            <button
              type="button"
              onClick={filter.onOpen}
              className="block w-full truncate text-left font-medium hover:underline"
              title={title}
            >
              {title}
            </button>
          </li>
        ))}
        {rest > 0 && (
          <li className="text-muted-foreground">{t('actions.andMore', { count: rest })}</li>
        )}
      </ul>
      <p className="mt-1 text-[10px] text-muted-foreground">
        {t('termBase.documentFilterShort', { count: filter.titles.length })}
      </p>
    </div>
  )
}
